import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { TeamRostersReportFields } from 'src/app/reports/team-rosters-report/team-rosters-report-fields';
import { TeamRostersReportModel } from 'src/app/reports/team-rosters-report/team-rosters-report.model';
import { CustomView } from 'src/app/shared/components/odata-filter/models/custom-view';
import { FilterOperatorType } from 'src/app/shared/components/odata-filter/models/filter-operator-type';
import { OPERATORS } from 'src/app/shared/components/odata-filter/models/operators';
import { SavedFilter } from 'src/app/shared/components/odata-filter/models/saved-filter';
import { SelectedFilter } from 'src/app/shared/components/odata-filter/models/selected-filter';
import { TEAM_ROSTERS_REPORT_FILTER_MODELS } from 'src/app/shared/components/odata-filter/models/system-filter-models';
import { ViewContext } from 'src/app/shared/components/odata-filter/models/view-context';
import { SeatStatus } from 'src/app/shared/models/team/seatStatus';
import { TeamFilterResult } from 'src/app/shared/models/team/teamFilterResult';
import { ApiVersions } from 'src/app/shared/services/api-version';
import { BaseODataService } from 'src/app/shared/services/base-odata.service';
import { last30Days } from 'src/app/shared/utils/date-utils';
import { TeamRegularFields } from 'src/app/teams/teams-home/team-regular-fields';

@Injectable({
  providedIn: 'root',
})
export class RecentAcceptsService extends BaseODataService {
  public readonly recentAccepts = new Subject<TeamRostersReportModel[]>();
  constructor() {
    super();

    this._baseUrl = 'teamroster';
    this._filterResult = new TeamFilterResult();
  }

  getRecentAccepts(view: CustomView) {
    let filters = this.convertTeamFiltersToTeamRosterFilters(view.filters).concat(
      this.generateFilters(SeatStatus.Accepted)
    );
    let queryParams = {
      select: [
        TeamRostersReportFields.seatId,
        TeamRostersReportFields.personId,
        TeamRostersReportFields.personName,
        TeamRostersReportFields.personImageId,
        TeamRostersReportFields.teamId,
        TeamRostersReportFields.teamName,
        TeamRostersReportFields.seatUpdatedOn,
        TeamRostersReportFields.teamWorkspaceId,
      ],
      filter: this.buildFilter([...filters]),
      orderBy: [`${TeamRostersReportFields.seatUpdatedOn} desc`],
    };

    this.getODataResult(this.allResultsFilterUrl, queryParams, ApiVersions.unversioned)
      .pipe(
        map((response) => {
          if (response && response.value) {
            return response.value.map((v) => new TeamRostersReportModel(v));
          } else {
            return [];
          }
        })
      )
      .subscribe((result) => this.recentAccepts.next(result));
  }

  getRecentInvitedPeopleCount(view: CustomView) {
    let filters = this.convertTeamFiltersToTeamRosterFilters(view.filters).concat(
      this.generateFilters(SeatStatus.Invited)
    );
    return this.getODataResultCountOnly(
      this.filterUrl,
      this.buildFilter([...filters]),
      ApiVersions.v1_0
    );
  }
  private generateFilters(seatStatus: SeatStatus) {
    return [
      new SelectedFilter({
        filter: TEAM_ROSTERS_REPORT_FILTER_MODELS.PositionStatusFilterModel,
        filterOperator: OPERATORS.List.operators.find(
          (op) => op.displayOperator == FilterOperatorType.eq
        ),
        searchValue: SeatStatus[seatStatus],
      }),
      new SelectedFilter({
        filter: TEAM_ROSTERS_REPORT_FILTER_MODELS.PositionUpdateDateFilterModel,
        filterOperator: OPERATORS.Date.operators.find(
          (op) => op.displayOperator == FilterOperatorType.gt
        ),
        searchValue: last30Days(),
      }),
    ];
  }

  private convertTeamFiltersToTeamRosterFilters(data: string[]) {
    let selectedFilters = SavedFilter.parseToAppliedFilters(data, ViewContext.Teams);
    return selectedFilters
      .map((selectedFilter) => {
        switch (selectedFilter.filter.targetField) {
          case TeamRegularFields.createdBy:
            selectedFilter.filter.targetField = TeamRostersReportFields.teamCreatedBy;
            break;
          case TeamRegularFields.createdOn:
            selectedFilter.filter.targetField = TeamRostersReportFields.teamCreatedOn;
            break;
          case TeamRegularFields.startDate:
            selectedFilter.filter.targetField = TeamRostersReportFields.teamStartDate;
            break;
          case TeamRegularFields.endDate:
            selectedFilter.filter.targetField = TeamRostersReportFields.teamEndDate;
            break;
          case TeamRegularFields.name:
            selectedFilter.filter.targetField = TeamRostersReportFields.teamName;
            break;
          case TeamRegularFields.status:
            selectedFilter.filter.targetField = TeamRostersReportFields.teamStatus;
            break;
          case TeamRegularFields.updatedBy:
            selectedFilter.filter.targetField = TeamRostersReportFields.teamUpdatedBy;
            break;
          case TeamRegularFields.updatedOn:
            selectedFilter.filter.targetField = TeamRostersReportFields.teamUpdatedOn;
            break;
          case TeamRegularFields.id:
            selectedFilter.filter.targetField = TeamRostersReportFields.teamId;
            break;
          case TeamRegularFields.customFields:
            selectedFilter.filter.targetField = TeamRostersReportFields.teamCustomFields;
            break;
          default:
            return null;
        }
        return selectedFilter;
      })
      .filter((x) => x != null);
  }
}
